import React, { memo } from 'react';
import { Control } from 'react-hook-form';
import { Box, makeStyles, Typography } from '@material-ui/core';
import { EvaluationFieldType, EvaluationFieldProps } from '../../types/Evaluation';
import {
  SingleSelectInput,
  MultiSelectInput,
  TextInput,
  SwitchInput,
  DateInput,
  NumberInput,
  MultiTextInput,
  ColorSwatch
} from '../../atoms/evaluation-inputs';
import { COLOR_DARK_GRAY } from '../../styles/colors';

const useStyles = makeStyles(theme => ({
  wrapper: {
    width: '100%',
  },
  lastEditLabel: {
    marginTop: theme.spacing(0.5),
    fontSize: theme.typography.pxToRem(11),
    color: COLOR_DARK_GRAY,
    lineHeight: 1.3,
  },
}), { name: 'FieldRenderer' });

/**
 * Maps each field type string to its corresponding input component.
 * To add a new field type, add an entry here and create the input component.
 */
const FIELD_COMPONENT_MAP: Record<EvaluationFieldType, React.ComponentType<EvaluationFieldProps>> = {
  Singleselect: SingleSelectInput,
  Multiselect: MultiSelectInput,
  Text: TextInput,
  Boolean: SwitchInput,
  Date: DateInput,
  Float: NumberInput,
  MultipleTexts: MultiTextInput,
  Color: ColorSwatch,
  NUMERIC: NumberInput,
  TEXT: TextInput,
  SELECT: SingleSelectInput,
  COLOR: ColorSwatch,
  TEXT_MULTI: MultiTextInput,
  DATE: DateInput,
  MULTISELECT: MultiSelectInput,
  NUMBER: NumberInput,
};

interface FieldRendererProps {
  name: string;
  label: string;
  type: EvaluationFieldType;
  control: Control<any>;
  selectionMessage?: string;
  options?: string[];
  readOnly?: string | boolean | null;
  possibleValues?: Record<string, any>;
  hideValue?: boolean;
  lastEditDate?: string;
}

/**
 * Dynamically renders the correct input component based on the field type.
 * Uses FIELD_COMPONENT_MAP instead of switch/if-else for scalability.
 */
function FieldRenderer({ name, label, type, control, selectionMessage, options, readOnly, possibleValues, hideValue, lastEditDate }: FieldRendererProps) {
  const classes = useStyles();
    const Component = FIELD_COMPONENT_MAP[type];

    if (!Component) {
        console.warn(`FieldRenderer: Unknown field type "${type}" for field "${label}"`);
        return null;
    }

    return (
      <Box className={classes.wrapper}>
        <Component
        name={name}
        control={control}
        label={label}
        selectionMessage={selectionMessage}
        options={options}
        readOnly={readOnly}
        possibleValues={possibleValues}
        hideValue={hideValue}
        />
            {lastEditDate && (
              <Typography className={classes.lastEditLabel}>
                Last edit : {lastEditDate}
              </Typography>
            )}
      </Box>
    );
}

export default memo(FieldRenderer);
